import { Injectable } from '@angular/core';
import { NotifyUtils } from '@app/shared/utils/notify.utils';
import { Observable, firstValueFrom } from 'rxjs';
import { QuestionTemplateMessages } from '../locale/question-template.messages';
import { Page } from '@app/models/api/page.model';
import { PagingParams } from '@app/models/api/paging-params.model';
import { SortingParams } from '@app/models/api/sorting-params.model';
import { QuestionTemplateAPIService } from '../api/question-template-api.service';
import { QuestionTemplate } from '../model/question-template.model';
import { CreateQuestionTemplate } from '../model/create-question-template.model';
import { QuestionTemplateUpdateResponse } from '../model/question-template-update-response.model';
import { InFlightRegistry } from '@app/shared/utils/in-flight-registry';

@Injectable({
  providedIn: 'root'
})
export class QuestionTemplateBusinessService {

  // key -> the write currently in flight for that template. A duplicate call while one is in flight
  // is coalesced onto the same promise (see InFlightRegistry) rather than rejected.
  private updating: InFlightRegistry;
  uploadingFile: boolean;

  constructor(
    private questionTemplateAPI: QuestionTemplateAPIService,
    private notifyUtils: NotifyUtils
  ) {
    this.updating = new InFlightRegistry();
    this.uploadingFile = false;
  }

  get(
    ids: number[],
    name: string,
    creatorIds: number[]
  ): Observable<QuestionTemplate[]> {
    return this.questionTemplateAPI
      .get(ids, name, creatorIds);
  }

  getPaginated(
    pagingParams: PagingParams,
    sortingParams: SortingParams,
    ids: number[],
    name: string,
    creatorIds: number[]
  ): Observable<Page<QuestionTemplate>> {
    return this.questionTemplateAPI.getPaginated(pagingParams, sortingParams, ids, name, creatorIds);
  }

  create(createDto: CreateQuestionTemplate): Promise<QuestionTemplate> {
    return firstValueFrom(this.questionTemplateAPI.create(createDto))
      .then(res => {
        this.notifyUtils.notify(QuestionTemplateMessages.SUCCESS_CREATE);
        return res;
      });
  }

  update(id: number, updateDto: CreateQuestionTemplate): Promise<QuestionTemplateUpdateResponse> {
    return this.updating.run(id, () =>
      firstValueFrom(this.questionTemplateAPI.update(id, updateDto))
        .then(res => {
          this.notifyUtils.notify(QuestionTemplateMessages.SUCCESS_UPDATE);
          return res;
        })
    );
  }

  updateApplicationsUsingTemplate(id: number): Promise<number> {
    return this.updating.run(id, () =>
      firstValueFrom(this.questionTemplateAPI.updateApplicationsUsingTemplate(id))
        .then(res => {
          this.notifyUtils.notify(`${res} applications updated`);
          return res;
        })
    );
  }

  delete(id: number): Promise<QuestionTemplate> {
    return this.updating.run(id, () =>
      firstValueFrom(this.questionTemplateAPI.delete(id))
        .then(res => {
          this.notifyUtils.notify(QuestionTemplateMessages.SUCCESS_DELETE);
          return res;
        })
    );
  }

  checkApplicationsUsingTemplate(id: number): Promise<number> {
    return firstValueFrom(this.questionTemplateAPI.checkApplicationsUsingTemplate(id));
  }
}
